<template>
  <div class="teacher-panel">
    <div class="panel-header">
      <div class="header-content">
        <div class="logo-section">
          <img src="@/assets/images/logo.png" alt="Modern Kurs Logo" class="header-logo" />
        </div>
      </div>
      <div class="header-actions">
        <a href="/kilavuzlar/ogretmen-kilavuz.pdf" target="_blank" rel="noopener" class="guide-link" title="Öğretmen Paneli Kullanım Kılavuzu">
          📖 Kullanım Kılavuzu
        </a>
        <div class="profile-info" @click="showProfileModal = true">
          <div class="profile-avatar">
            {{ getInitials(currentUser?.ad, currentUser?.soyad) }}
          </div>
          <div class="profile-details">
            <span class="profile-name">{{ currentUser?.ad }} {{ currentUser?.soyad }}</span>
            <span class="profile-role">Öğretmen</span>
          </div>
          <div class="profile-arrow">▼</div>
        </div>
      </div>
    </div>
    
    <div class="panel-content">
      <!-- Hoş Geldiniz Kartı -->
      <div v-if="currentUser" class="welcome-card">
        <div class="welcome-avatar">👤</div>
        <div class="welcome-info">
          <h2>Hoş Geldiniz, {{ currentUser.ad }} {{ currentUser.soyad }}!</h2>
          <p class="welcome-role">Öğretmen</p>
          <p class="welcome-message">{{ currentWeekNumber }}. hafta programınızı yönetebilirsiniz.</p>
        </div>
        <div class="welcome-stats">
          <div class="stat-item">
                            <span class="stat-number">{{ thisWeekConfirmedAppointments.length }}</span>
            <span class="stat-label">Bu Hafta</span>
          </div>
          <div class="stat-item">
            <span class="stat-number">{{ totalAppointments }}</span>
            <span class="stat-label">Toplam</span>
          </div>
        </div>
      </div>
      
      <!-- Hızlı Erişim Kartları -->
      <div class="quick-access-grid">

        <div class="quick-card secondary" @click="scrollToAppointments">
          <div class="card-icon">📋</div>
          <h3>Randevularım</h3>
          <p>Öğrenci randevularınızı görüntüleyin</p>
        </div>

        <div class="quick-card primary" @click="scrollToAvailability">
          <div class="card-icon">📅</div>
          <h3>Müsaitlik Takvimi</h3>
          <p>Haftalık müsaitlik takviminizi görüntüleyin</p>
        </div>

        <div class="quick-card info" @click="scrollToSchedule">
          <div class="card-icon">🗓️</div>
          <h3>Haftalık Program</h3>
          <p>Bu haftanın programını görüntüleyin</p>
        </div>
        </div>
        
      <!-- Bu Haftaki Randevular -->
      <div id="appointments-section" class="section-card">
        <div class="section-header">
          <h3>📋 Bu Haftaki Randevular</h3>
          <p>Bu hafta için planlanan öğrenci randevularınız</p>
        </div>
        <div class="section-content">
          <div v-if="thisWeekAppointments.length === 0" class="no-data">
            <div class="no-data-icon">📭</div>
            <p>Bu hafta için henüz randevu bulunmamaktadır.</p>
          </div>
          <div v-else class="appointments-list">
            <div 
              v-for="appointment in thisWeekAppointments" 
              :key="appointment.id"
              class="appointment-item"
            >
              <div class="appointment-time">
                <div class="time-main">{{ formatTime(appointment.appointment_time) }}</div>
                <div class="time-day">{{ formatDay(appointment.appointment_time) }}</div>
              </div>
              <div class="appointment-details">
                <h4>{{ appointment.lesson?.name || 'Bilinmeyen Ders' }}</h4>
                <p class="student-name">{{ appointment.user?.ad }} {{ appointment.user?.soyad }}</p>
                <span :class="['status-badge', 'status-' + appointment.status]">
                  {{ getStatusText(appointment.status) }}
                </span>
              </div>
              
              <!-- Katılmadı/Katıldı Butonları -->
              <div class="appointment-actions">
                <button 
                  v-if="appointment.status === 'onaylandı'"
                  @click="markStudentAbsent(appointment.id)"
                  class="btn-mark-absent"
                  :disabled="isUpdatingStatus"
                >
                  Katılmadı
                </button>
                
                <button 
                  v-if="appointment.status === 'öğrenci katılmadı'"
                  @click="markStudentPresent(appointment.id)"
                  class="btn-mark-present"
                  :disabled="isUpdatingStatus"
                >
                  ✅ Katıldı Olarak İşaretle
                </button>
              </div>
              
            </div>
          </div>
        </div>
      </div>
            <!-- Müsaitlik Yönetimi -->
            <div id="availability-section" class="section-card">
        <div class="section-header">
          <h3>📅 Müsaitlik Takvimi</h3>
          <p>Bu hafta için müsaitlik takviminizi görüntüleyin</p>
        </div>
        <div class="section-content">
          <div v-if="loading" class="loading-container">
            <div class="loading-spinner"></div>
            <p>Müsaitlik bilgileri yükleniyor...</p>
      </div>
          <div v-else-if="!currentUser?.id" class="no-data">
            <div class="no-data-icon">⚠️</div>
            <p>Kullanıcı bilgisi yüklenemedi.</p>
            <button @click="retryLoad" class="retry-button">Tekrar Dene</button>
          </div>
          <div v-else>
            <TeacherAvailabilityDisplay 
              :teacherId="currentUser.id"
              :viewMode="'view'"
              :compact="false"
            />
          </div>
        </div>
      </div>

      <!-- Haftalık Program Özeti -->
      <div id="schedule-section" class="section-card">
        <div class="section-header">
          <h3>🗓️ Haftalık Program Özeti</h3>
          <p>{{ formatWeekRange() }} tarihleri arasındaki programınız</p>
        </div>
        <div class="section-content">
          <div v-if="!weeklyStats" class="no-data">
            <div class="no-data-icon">📊</div>
            <p>Bu hafta için henüz randevu verisi bulunmamaktadır.</p>
          </div>
          
          <div v-else class="schedule-summary">
            <!-- Hafta Bilgisi -->
            <div class="week-info-header">
              <h4>📅 {{ weeklyStats.weekStart }} - {{ weeklyStats.weekEnd }}</h4>
              <span class="week-number">{{ currentWeekNumber }}. Hafta</span>
            </div>
            
            <!-- Genel İstatistikler -->
            <div class="stats-grid">
              <div class="stat-card primary">
                <div class="stat-icon">📋</div>
                <div class="stat-content">
                  <div class="stat-number">{{ weeklyStats.totalAppointments }}</div>
                  <div class="stat-label">Toplam Randevu</div>
                </div>
              </div>
              
              <div class="stat-card success">
                <div class="stat-icon">✅</div>
                <div class="stat-content">
                  <div class="stat-number">{{ weeklyStats.confirmedAppointments }}</div>
                  <div class="stat-label">Onaylanmış</div>
                </div>
              </div>
              
              <div class="stat-card warning">
                <div class="stat-icon">❌</div>
                <div class="stat-content">
                  <div class="stat-number">{{ weeklyStats.cancelledAppointments }}</div>
                  <div class="stat-label">İptal Edilen</div>
                </div>
              </div>
              
              <div class="stat-card danger">
                <div class="stat-icon">⏰</div>
                <div class="stat-content">
                  <div class="stat-number">{{ weeklyStats.absentAppointments }}</div>
                  <div class="stat-label">Katılmadı</div>
                </div>
              </div>
            </div>
            
            <!-- Günlük Detaylar -->
            <div class="daily-breakdown">
              <h5>📊 Günlük Dağılım</h5>
              <div class="daily-stats">
                <div v-for="(stats, day) in weeklyStats.dailyStats" :key="day" class="day-stat">
                  <div class="day-name">{{ day }}</div>
                  <div class="day-numbers">
                    <span class="total">{{ stats.total }}</span>
                    <span v-if="stats.confirmed > 0" class="confirmed">+{{ stats.confirmed }}</span>
                    <span v-if="stats.cancelled > 0" class="cancelled">-{{ stats.cancelled }}</span>
                    <span v-if="stats.absent > 0" class="absent">~{{ stats.absent }}</span>
                  </div>
                </div>
              </div>
            </div>
            

          </div>
        </div>
      </div>
    </div>

    <!-- Profil Modal -->
    <div v-if="showProfileModal" class="modal-overlay" @click="showProfileModal = false">
      <div class="modal-content profile-modal" @click.stop>
        <div class="modal-header">
          <h3>👤 Profil Bilgileri</h3>
          <button @click="showProfileModal = false" class="modal-close">&times;</button>
        </div>
        <div class="modal-body">
          <div class="profile-section">
            <div class="profile-avatar-large">
              {{ getInitials(currentUser?.ad, currentUser?.soyad) }}
            </div>
            <div class="profile-info-details">
              <div class="info-row">
                <span class="info-label">Ad Soyad:</span>
                <span class="info-value">{{ currentUser?.ad }} {{ currentUser?.soyad }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Kullanıcı Adı:</span>
                <span class="info-value">{{ currentUser?.username }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Rol:</span>
                <span class="info-value">{{ currentUser?.role }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Hafta:</span>
                <span class="info-value">{{ currentWeekNumber }}. Hafta</span>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-actions">
          <button @click="logout" class="logout-button">🚪 Çıkış Yap</button>
          <button @click="showProfileModal = false" class="btn-secondary">Kapat</button>
        </div>
      </div>
    </div>
    
    <!-- Profil Modal -->
    <div v-if="showProfileModal" class="modal-overlay" @click="showProfileModal = false">
      <div class="modal-content profile-modal" @click.stop>
        <div class="modal-header">
          <h3>👤 Profil Bilgileri</h3>
          <button @click="showProfileModal = false" class="modal-close">&times;</button>
        </div>
        <div class="modal-body">
          <div class="profile-section">
            <div class="profile-avatar-large">
              {{ getInitials(currentUser?.ad, currentUser?.soyad) }}
            </div>
            <div class="profile-actions">
            <button @click="logout" class="logout-button">
              🚪 Çıkış Yap
            </button>
          </div>
            <div class="profile-info-grid">
              <div class="info-item">
                <label>Ad Soyad:</label>
                <span>{{ currentUser?.ad }} {{ currentUser?.soyad }}</span>
              </div>
              <div class="info-item">
                <label>Kullanıcı Adı:</label>
                <span>{{ currentUser?.username }}</span>
              </div>
              <div class="info-item">
                <label>Rol:</label>
                <span class="role-badge">👤 Öğretmen</span>
              </div>
              <div class="info-item">
                <label>Yeni Şifre:</label>
                <div class="password-input-group">
                  <input 
                    v-model="newPassword" 
                    type="password" 
                    placeholder="Yeni şifre girin"
                    class="password-input"
                  />
                  <button @click="updatePassword" class="update-password-btn" :disabled="!newPassword || isUpdatingPassword">
                    <span v-if="!isUpdatingPassword">🔄 Güncelle</span>
                    <span v-else>⏳ Güncelleniyor...</span>
                  </button>
                </div>
              </div>

            </div>
          </div>
          
          <div class="profile-stats">
            <div class="stat-card">
              <div class="stat-icon">📅</div>
              <div class="stat-content">
                <span class="stat-number">{{ thisWeekConfirmedAppointments.length }}</span>
                <span class="stat-label">Bu Hafta</span>
              </div>
            </div>
            <div class="stat-card">
              <div class="stat-icon">📋</div>
              <div class="stat-content">
                <span class="stat-number">{{ totalAppointments }}</span>
                <span class="stat-label">Toplam Randevu</span>
              </div>
            </div>
            <div class="stat-card">
              <div class="stat-icon">🎯</div>
              <div class="stat-content">
                <span class="stat-number">{{ currentWeekNumber }}</span>
                <span class="stat-label">Hafta</span>
              </div>
            </div>
        </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Çıkış Onay Popup -->
  <div v-if="showLogoutConfirmPopup" class="logout-confirm-popup-overlay" @click="cancelLogout">
    <div class="logout-confirm-popup" @click.stop>
      <div class="logout-confirm-popup-header">
        <div class="logout-icon">🚪</div>
        <h3>Çıkış Onayı</h3>
      </div>
      <div class="logout-confirm-popup-body">
        <p>Çıkış yapmak istediğinizden emin misiniz?</p>
      </div>
      <div class="logout-confirm-popup-actions">
        <button @click="cancelLogout" class="btn-cancel">
          İptal
        </button>
        <button @click="confirmLogout" class="btn-logout-confirm">
          Evet, Çıkış Yap
        </button>
      </div>
    </div>
  </div>
  <!-- Modern Student Absent Confirmation Modal -->
  <div v-if="showAbsentModal" class="modal-overlay" @click="closeAbsentModal">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <div class="modal-icon">❌</div>
        <h3>Öğrenci Katılmadı</h3>
        <button class="modal-close" @click="closeAbsentModal">×</button>
      </div>
      
      <div class="modal-body">
        <div class="warning-section">
          <div class="warning-icon">⚠️</div>
          <div class="warning-content">
            <h4>Dikkat!</h4>
            <p>Öğrencinin bu randevuya katılmadığını işaretlemek istediğinizden emin misiniz?</p>
          </div>
        </div>
        
        <div class="info-section">
          <div class="info-icon">ℹ️</div>
          <div class="info-content">
            <h4>Ne olacak?</h4>
            <ul>
              <li>Randevu durumu "Öğrenci Katılmadı" olarak değişecek</li>
              <li>Bu işlem geri alınabilir</li>
              <li>Öğrenci tekrar randevu alabilecek</li>
            </ul>
          </div>
        </div>
      </div>
      
      <div class="modal-footer">
        <button class="btn-cancel" @click="closeAbsentModal">
          İptal
        </button>
        <button class="btn-confirm" @click="confirmMarkAbsent" :disabled="isUpdatingStatus">
          <span v-if="isUpdatingStatus">İşleniyor...</span>
          <span v-else>Evet, Katılmadı Olarak İşaretle</span>
        </button>
      </div>
    </div>
  </div>
  
  <!-- Modern Student Present Confirmation Modal -->
  <div v-if="showPresentModal" class="modal-overlay" @click="closePresentModal">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <div class="modal-icon">✅</div>
        <h3>Öğrenci Katıldı</h3>
        <button class="modal-close" @click="closePresentModal">×</button>
      </div>
      
      <div class="modal-body">
        <div class="info-section">
          <div class="info-icon">ℹ️</div>
          <div class="info-content">
            <h4>Bilgi</h4>
            <p>Öğrencinin katıldığını işaretlemek istediğinizden emin misiniz?</p>
          </div>
        </div>
        
        <div class="success-section">
          <div class="success-icon">🎯</div>
          <div class="success-content">
            <h4>Ne olacak?</h4>
            <ul>
              <li>Randevu durumu "Tamamlandı" olarak değişecek</li>
              <li>Öğrenci katılımı onaylanacak</li>
              <li>Bu işlem geri alınabilir</li>
            </ul>
          </div>
        </div>
      </div>
      
      <div class="modal-footer">
        <button class="btn-cancel" @click="closePresentModal">
          İptal
        </button>
        <button class="btn-success" @click="confirmMarkPresent" :disabled="isUpdatingStatus">
          <span v-if="isUpdatingStatus">İşleniyor...</span>
          <span v-else>Evet, Katıldı Olarak İşaretle</span>
        </button>
      </div>
    </div>
  </div>
  
  <AppSuccessPopup :show="showSuccessModal" :title="successTitle" :message="successMessage" @close="closeSuccessModal" />
  <AppErrorModal :message="showErrorModal ? errorMessage : ''" :title="errorTitle" @close="closeErrorModal" />
</template>

<script>
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { ref, onMounted, onUnmounted, computed } from 'vue'
import axios from 'axios'
import { isoWeek } from '@/utils/week'
import TeacherAvailabilityDisplay from '@/components/TeacherAvailabilityDisplay.vue'
import AppSuccessPopup from '@/components/AppSuccessPopup.vue'
import AppErrorModal from '@/components/AppErrorModal.vue'

export default {
  name: 'TeacherPanel',
  components: {
    TeacherAvailabilityDisplay,
    AppSuccessPopup,
    AppErrorModal
  },
  setup() {
    const router = useRouter()
    const store = useStore()
    
    const currentUser = ref(null)
    const loading = ref(false)
    const appointments = ref([])
    const currentWeekNumber = ref(0)

    // Yetkisiz erişim gibi durumlarda hata modal'ı kapanınca yapılacak yönlendirme
    const pendingRedirect = ref(null)
    const showProfileModal = ref(false)
    
    // Şifre güncelleme için
    const newPassword = ref('')
    const isUpdatingPassword = ref(false)
    
    // Çıkış onay popup durumu
    const showLogoutConfirmPopup = ref(false)

    const goHome = () => {
      router.push('/')
    }

    const loadCurrentUser = async () => {
      try {
        // Önce token geçerliliğini kontrol et
        const isTokenValid = await store.dispatch('checkTokenValidity')
        if (!isTokenValid) {
          console.error('❌ Geçersiz token')
          router.push('/login')
          return
        }
        
        // Local storage'dan kullanıcı bilgisini al
        const userStr = localStorage.getItem('user')
        if (userStr) {
          const userData = JSON.parse(userStr)
          
          // Rol kontrolü - sadece öğretmen erişebilir (backend'den gelen 'hoca' rolü)
          if (userData.role !== 'teacher' && userData.role !== 'hoca') {
            console.error('❌ Yetkisiz erişim: Öğretmen olmayan kullanıcı öğretmen paneline erişmeye çalışıyor')
            showError('Yetkisiz Erişim', 'Bu sayfaya erişim yetkiniz bulunmamaktadır. Sadece öğretmenler erişebilir.')
            pendingRedirect.value = '/login'
            return
          }
          
          currentUser.value = userData
  
        } else {
          console.error('❌ Local storage\'da kullanıcı bilgisi bulunamadı')
          // Login sayfasına yönlendir
          router.push('/login')
        }
      } catch (error) {
        console.error('❌ Kullanıcı bilgisi yüklenirken hata:', error)
        router.push('/login')
      }
    }

    const loadTeacherData = async () => {
      if (!currentUser.value?.id) {
        console.error('❌ currentUser.id bulunamadı:', currentUser.value)
        return
      }
      
      loading.value = true
      try {

        
        // Randevuları yükle - Hoca olarak giriş yapıldığında, o hocanın verdiği derslerin randevularını çek
        const appointmentsResult = await store.dispatch('fetchAllAppointments')

        
        // Hoca olarak giriş yapıldığında, o hocanın verdiği derslerin randevularını al
        const allAppointments = store.state.appointments['all'] || []
        appointments.value = allAppointments.filter(apt => apt.teacher_user_id === currentUser.value.id)

        
        // Hafta numarasını hesapla
        currentWeekNumber.value = getWeekNumber(new Date())

        
      } catch (error) {
        console.error('❌ Hoca verileri yüklenirken hata:', error)
      } finally {
        loading.value = false
      }
    }



    // Bu haftaki randevular (tüm durumlar - listede gösterilmek için)
    const thisWeekAppointments = computed(() => {
      if (!appointments.value.length) return []
      
      const now = new Date()
      const weekStart = getWeekStartDate(now)
      const weekEnd = new Date(weekStart)
      weekEnd.setDate(weekStart.getDate() + 7)
      
      return appointments.value.filter(appointment => {
        const appointmentDate = new Date(appointment.appointment_time)
        return appointmentDate >= weekStart && 
               appointmentDate < weekEnd && 
               appointment.teacher_user_id === currentUser.value?.id
      }).sort((a, b) => new Date(a.appointment_time) - new Date(b.appointment_time))
    })

    // Bu haftaki onaylanmış randevular (sadece sayım için)
    const thisWeekConfirmedAppointments = computed(() => {
      return thisWeekAppointments.value.filter(appointment => 
        appointment.status === 'onaylandı'
      )
    })

    // Toplam onaylanmış randevu sayısı
    const totalAppointments = computed(() => {
      return appointments.value.filter(app => 
        app.teacher_user_id === currentUser.value?.id && 
        app.status === 'onaylandı'
      ).length
    })



    // Haftalık program istatistikleri - Gelişmiş
    const weeklyStats = computed(() => {
      if (!appointments.value.length) return null
      
      const now = new Date()
      const weekStart = getWeekStartDate(now)
      const weekEnd = new Date(weekStart)
      weekEnd.setDate(weekStart.getDate() + 7)
      
      // Bu haftaki randevuları filtrele
      const weekAppointments = appointments.value.filter(apt => {
        const aptDate = new Date(apt.appointment_time)
        return aptDate >= weekStart && aptDate < weekEnd
      })
      
      // Günlük randevu sayıları
      const dailyStats = {}
      const weekdays = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar']
      
      weekdays.forEach(day => {
        dailyStats[day] = {
          total: 0,
          confirmed: 0,
          cancelled: 0,
          absent: 0
        }
      })
      
      weekAppointments.forEach(apt => {
        const aptDate = new Date(apt.appointment_time)
        const dayIndex = aptDate.getDay()
        const dayName = weekdays[dayIndex === 0 ? 6 : dayIndex - 1] // Pazartesi = 0, Pazar = 6

        switch (apt.status) {
          case 'onaylandı':
            dailyStats[dayName].confirmed++
            dailyStats[dayName].total++ // Sadece aktif/tamamlanmış randevu 'toplam'a sayılır
            break
          case 'iptal edildi':
            dailyStats[dayName].cancelled++
            break
          case 'öğrenci katılmadı':
            dailyStats[dayName].absent++
            break
        }
      })
      
      // En yoğun gün
      const busiestDay = Object.entries(dailyStats).reduce((max, [day, stats]) => {
        return stats.total > max.total ? { day, ...stats } : max
      }, { day: 'Yok', total: 0 })
      
      // En az yoğun gün
      const quietestDay = Object.entries(dailyStats).reduce((min, [day, stats]) => {
        return stats.total < min.total ? { day, ...stats } : min
      }, { day: 'Yok', total: 0 })
      
      // Toplam istatistikler
      // Backend GetAdminStatistics ile aynı tanım: iptal edilen ve katılmayan randevular
      // 'toplam' sayımından hariç tutulur (aktif/tamamlanmış randevular sayılır).
      const confirmedAppointments = weekAppointments.filter(apt => apt.status === 'onaylandı').length
      const cancelledAppointments = weekAppointments.filter(apt => apt.status === 'iptal edildi').length
      const absentAppointments = weekAppointments.filter(apt => apt.status === 'öğrenci katılmadı').length
      const totalAppointments = confirmedAppointments
      
      // Doluluk oranı (onaylanmış randevular / toplam müsait saat)
      const totalAvailableHours = 40 // 5 gün x 8 saat (20 dakikalık dilimler)
      const occupancyRate = totalAvailableHours > 0 ? Math.round((confirmedAppointments / totalAvailableHours) * 100) : 0

    return {
        weekStart: weekStart.toLocaleDateString('tr-TR'),
        weekEnd: weekEnd.toLocaleDateString('tr-TR'),
        totalAppointments,
        confirmedAppointments,
        cancelledAppointments,
        absentAppointments,
        dailyStats,
        busiestDay,
        quietestDay,
        occupancyRate,
        averagePerDay: totalAppointments > 0 ? Math.round((totalAppointments / 7) * 10) / 10 : 0
      }
    })

    // Yardımcı fonksiyonlar
    const getWeekStartDate = (date) => {
      // Turkey timezone (+3) için tarihi ayarla
      const turkeyDate = new Date(date.toLocaleString("en-US", {timeZone: "Europe/Istanbul"}))
      
      // Pazar 19:00 kuralı: Pazar 19:00'dan sonra gelecek haftayı göster
      const isSundayAfter7PM = turkeyDate.getDay() === 0 && turkeyDate.getHours() >= 19
      
      if (isSundayAfter7PM) {
        // Pazar 19:00'dan sonra: Gelecek haftayı göster
        turkeyDate.setDate(turkeyDate.getDate() + 1) // Pazartesi'ye geç
      }
      
      const weekday = turkeyDate.getDay()
      const daysToMonday = weekday === 0 ? 6 : weekday - 1
      const monday = new Date(turkeyDate)
      monday.setDate(turkeyDate.getDate() - daysToMonday)
      
      return monday
    }

    // Hafta numarası hesaplama — ISO 8601 (backend utils.ISOWeek ile birebir)
    const getWeekNumber = (date) => {
      return isoWeek(date).week
    }

    const formatTime = (dateTime) => {
      return new Date(dateTime).toLocaleTimeString('tr-TR', { 
        hour: '2-digit', 
        minute: '2-digit' 
      })
    }

    const formatDay = (dateTime) => {
      return new Date(dateTime).toLocaleDateString('tr-TR', { 
        weekday: 'short',
        day: '2-digit',
        month: '2-digit'
      })
    }

    const formatWeekRange = () => {
      const now = new Date()
      const weekStart = getWeekStartDate(now)
      const weekEnd = new Date(weekStart)
      weekEnd.setDate(weekStart.getDate() + 6)
      
      return `${weekStart.toLocaleDateString('tr-TR')} - ${weekEnd.toLocaleDateString('tr-TR')}`
    }

    const getStatusText = (status) => {
      const statusMap = {
        'beklemede': 'Beklemede',
        'onaylandı': 'Onaylandı',
        'iptal edildi': 'İptal Edildi',
        'öğrenci katılmadı': 'Öğrenci Katılmadı'
      }
      return statusMap[status] || status
    }

    // Şifre güncelleme fonksiyonu
    const updatePassword = async () => {
      if (!newPassword.value) {
        showError('Uyarı', 'Şifre boş olamaz.')
        return
      }

      try {
        isUpdatingPassword.value = true

        const response = await axios.post('/api/users/me/change-password', {
          password: newPassword.value
        }, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        })

        if (response.data) {
          showSuccess('🔑 Şifre Güncellendi', 'Şifreniz başarıyla güncellendi!')
          newPassword.value = ''
        }
      } catch (err) {
        console.error('Şifre güncellenirken hata:', err)
        showError('Şifre Hatası', `Şifre güncellenirken hata: ${err.response?.data?.error || 'Bilinmeyen hata'}`)
      } finally {
        isUpdatingPassword.value = false
      }
    }

    // Hoca randevularını iptal edemez - bu fonksiyon kaldırıldı

    // Scroll fonksiyonları
    const scrollToAvailability = () => {
      document.getElementById('availability-section').scrollIntoView({ behavior: 'smooth' })
    }

    const scrollToAppointments = () => {
      document.getElementById('appointments-section').scrollIntoView({ behavior: 'smooth' })
    }



    const scrollToSchedule = () => {
      document.getElementById('schedule-section').scrollIntoView({ behavior: 'smooth' })
    }

    const getInitials = (ad, soyad) => {
      if (!ad || !soyad) return '?'
      return `${ad.charAt(0)}${soyad.charAt(0)}`.toUpperCase()
    }

    const logout = async () => {
      // Çıkış onay popup'ını göster
      showLogoutConfirmPopup.value = true
    }

    const confirmLogout = async () => {
      try {
        await store.dispatch('logout')
        router.push('/login')
      } catch (error) {
        console.error('Çıkış yapılırken hata:', error)
        // Hata olsa bile login sayfasına yönlendir
        router.push('/login')
      } finally {
        showLogoutConfirmPopup.value = false
      }
    }

    const cancelLogout = () => {
      showLogoutConfirmPopup.value = false
    }

    const retryLoad = async () => {
      
      await loadCurrentUser()
      if (currentUser.value?.id) {
        await loadTeacherData()
      }
    }

    // Randevu durumu güncelleme için ref
    const isUpdatingStatus = ref(false)
    
    // Modal state'leri
    const showAbsentModal = ref(false)
    const showPresentModal = ref(false)
    const selectedAppointmentId = ref(null)
    
    // Success ve Error modal state'leri
    const showSuccessModal = ref(false)
    const showErrorModal = ref(false)
    const successTitle = ref('')
    const successMessage = ref('')
    const errorTitle = ref('')
    const errorMessage = ref('')

    // Öğrenci katılmadı olarak işaretle
    const markStudentAbsent = async (appointmentId) => {
      selectedAppointmentId.value = appointmentId
      showAbsentModal.value = true
    }
    
    const closeAbsentModal = () => {
      showAbsentModal.value = false
      selectedAppointmentId.value = null
    }
    
    const confirmMarkAbsent = async () => {
      if (!selectedAppointmentId.value) return
      
      isUpdatingStatus.value = true
      try {
        const response = await axios.put(`https://api.modernkurs.com/api/appointments/${selectedAppointmentId.value}/mark-absent`, {}, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        })

        if (response.data.success) {
          // Randevuları yeniden yükle
          await loadTeacherData()
          showSuccess('Başarılı!', 'Öğrenci katılmadı olarak işaretlendi')
        } else {
          showError('Hata!', response.data.error || 'Bilinmeyen hata')
        }
      } catch (error) {
        console.error('Randevu durumu güncellenirken hata:', error)
        showError('Hata!', 'Randevu durumu güncellenirken bir hata oluştu')
      } finally {
        isUpdatingStatus.value = false
        closeAbsentModal()
      }
    }

    // Öğrenci katıldı olarak işaretle (geri alma)
    const markStudentPresent = async (appointmentId) => {
      selectedAppointmentId.value = appointmentId
      showPresentModal.value = true
    }
    
    const closePresentModal = () => {
      showPresentModal.value = false
      selectedAppointmentId.value = null
    }
    
    const closeSuccessModal = () => {
      showSuccessModal.value = false
    }
    
    const closeErrorModal = () => {
      showErrorModal.value = false
      if (pendingRedirect.value) {
        const target = pendingRedirect.value
        pendingRedirect.value = null
        router.push(target)
      }
    }
    
    const showSuccess = (title, message) => {
      successTitle.value = title
      successMessage.value = message
      showSuccessModal.value = true
    }
    
    const showError = (title, message) => {
      errorTitle.value = title
      errorMessage.value = message
      showErrorModal.value = true
    }
    
    const confirmMarkPresent = async () => {
      if (!selectedAppointmentId.value) return
      
      isUpdatingStatus.value = true
      try {
        const response = await axios.put(`https://api.modernkurs.com/api/appointments/${selectedAppointmentId.value}/mark-present`, {}, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        })

        if (response.data.success) {
          // Randevuları yeniden yükle
          await loadTeacherData()
          showSuccess('Başarılı!', 'Öğrenci katıldı olarak işaretlendi')
        } else {
          showError('Hata!', response.data.error || 'Bilinmeyen hata')
        }
      } catch (error) {
        console.error('Randevu durumu güncellenirken hata:', error)
        showError('Hata!', 'Randevu durumu güncellenirken bir hata oluştu')
      } finally {
        isUpdatingStatus.value = false
        closePresentModal()
      }
    }

    onMounted(async () => {
      // Önce route kontrolü yap - sadece hoca panelinde çalışsın
      if (router.currentRoute.value.path !== '/panel/hoca') {
        console.log('🚫 TeacherPanel: Hoca paneli dışında yüklendi, API çağrıları yapılmayacak')
        return
      }
      
      await loadCurrentUser()
      
      // Kullanıcı bilgisi yüklendikten sonra verileri yükle
      if (currentUser.value?.id) {

        await loadTeacherData()
      } else {
        console.error('❌ Kullanıcı bilgisi yüklenemedi, veriler yüklenmiyor')
      }
      
      // Sayfa focus olduğunda kullanıcı bilgilerini kontrol et
      window.addEventListener('focus', handleWindowFocus)
    })
    
    // Component unmount olduğunda event listener'ı temizle
    onUnmounted(() => {
      window.removeEventListener('focus', handleWindowFocus)
    })
    
    // Sayfa focus olduğunda çalışacak fonksiyon
    const handleWindowFocus = async () => {
      // Token geçerliliğini kontrol et
      const isTokenValid = await store.dispatch('checkTokenValidity')
      if (!isTokenValid) {

        router.push('/login')
        return
      }
      
      // Kullanıcı rolünü tekrar kontrol et
      if (currentUser.value && currentUser.value.role !== 'teacher' && currentUser.value.role !== 'hoca') {

        showError('Yetkisiz Erişim', 'Bu sayfaya erişim yetkiniz bulunmamaktadır.')
        pendingRedirect.value = '/login'
        return
      }
    }

    return {
      currentUser,
      loading,
      appointments,
      currentWeekNumber,
      thisWeekAppointments,
      thisWeekConfirmedAppointments,
      totalAppointments,
      weeklyStats,
      formatTime,
      formatDay,
      formatWeekRange,
      getStatusText,
      scrollToAvailability,
      scrollToAppointments,
      scrollToSchedule,
      showProfileModal,
      showLogoutConfirmPopup,
      newPassword,
      isUpdatingPassword,
      getInitials,
      logout,
      confirmLogout,
      cancelLogout,
      handleWindowFocus,
      retryLoad,
      updatePassword,
      markStudentAbsent,
      markStudentPresent,
      isUpdatingStatus,
      
      // Modal state'leri ve fonksiyonları
      showAbsentModal,
      showPresentModal,
      closeAbsentModal,
      closePresentModal,
      confirmMarkAbsent,
      confirmMarkPresent,
      
      // Success ve Error modal'ları
      showSuccessModal,
      showErrorModal,
      closeSuccessModal,
      closeErrorModal,
      successTitle,
      successMessage,
      errorTitle,
      errorMessage
    }
  }
}
</script>

<style scoped>
/* Modern Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1rem;
}

.modal-content {
  background: white;
  border-radius: 20px;
  box-shadow: 0 25px 50px rgba(255, 255, 255, 0.25);
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  animation: modalSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem 1.5rem 1rem;
  border-bottom: 1px solid #f1f3f4;
  position: relative;
}

.modal-icon {
  font-size: 2rem;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 15px;
  color: white;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  flex: 1;
}

.modal-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #666;
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.modal-close:hover {
  background: #f1f3f4;
  color: #333;
}

.modal-body {
  padding: 1.5rem;
}

.warning-section,
.info-section,
.success-section {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  border-radius: 12px;
}

.warning-section {
  background: #fff3cd;
  border: 1px solid #ffeaa7;
}

.info-section {
  background: #d1ecf1;
  border: 1px solid #bee5eb;
}

.success-section {
  background: #d4edda;
  border: 1px solid #c3e6cb;
}

.warning-icon,
.info-icon,
.success-icon {
  font-size: 1.5rem;
  flex-shrink: 0;
}

.warning-content h4,
.info-content h4,
.success-content h4 {
  margin: 0 0 0.5rem 0;
  font-size: 1.1rem;
  font-weight: 600;
}

.warning-content h4 {
  color: #856404;
}

.info-content h4 {
  color: #0c5460;
}

.success-content h4 {
  color: #155724;
}

.warning-content p,
.info-content p,
.success-content p {
  margin: 0;
  line-height: 1.5;
}

.warning-content p {
  color: #856404;
}

.info-content p {
  color: #0c5460;
}

.success-content p {
  color: #155724;
}

.warning-content ul,
.info-content ul,
.success-content ul {
  margin: 0.5rem 0 0 0;
  padding-left: 1.2rem;
}

.warning-content ul {
  color: #856404;
}

.info-content ul {
  color: #0c5460;
}

.success-content ul {
  color: #155724;
}

.warning-content li,
.info-content li,
.success-content li {
  margin-bottom: 0.3rem;
  line-height: 1.4;
}

.modal-footer {
  display: flex;
  gap: 1rem;
  padding: 1rem 1.5rem 1.5rem;
  border-top: 1px solid #f1f3f4;
  justify-content: flex-end;
}

.btn-cancel,
.btn-confirm,
.btn-success {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 120px;
}

.btn-cancel {
  background: #f8f9fa;
  color: #6c757d;
  border: 1px solid #dee2e6;
}

.btn-cancel:hover {
  background: #e9ecef;
  color: #495057;
  transform: translateY(-1px);
}

.btn-confirm {
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(220, 53, 69, 0.3);
}

.btn-confirm:hover:not(:disabled) {
  background: linear-gradient(135deg, #c82333 0%, #a71e2a 100%);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(220, 53, 69, 0.4);
}

.btn-success {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(40, 167, 69, 0.3);
}

.btn-success:hover:not(:disabled) {
  background: linear-gradient(135deg, #218838 0%, #1ea085 100%);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(40, 167, 69, 0.4);
}

  .btn-confirm:disabled,
  .btn-success:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
  
  /* Success ve Error Modal Stilleri */
  .success-modal .modal-icon {
    background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  }
  
  .error-modal .modal-icon {
    background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  }
  
  .success-message,
  .error-message {
    text-align: center;
    padding: 1rem;
  }
  
  .success-message p {
    color: #155724;
    font-size: 1.1rem;
    margin: 0;
  }
  
  .error-message p {
    color: #721c24;
    font-size: 1.1rem;
    margin: 0;
  }
  
  .btn-danger {
    background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
    color: white;
    box-shadow: 0 4px 15px rgba(220, 53, 69, 0.3);
  }
  
  .btn-danger:hover:not(:disabled) {
    background: linear-gradient(135deg, #c82333 0%, #a71e2a 100%);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(220, 53, 69, 0.4);
  }
  
  .btn-danger:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }

/* Responsive Modal */
@media (max-width: 768px) {
  .modal-content {
    max-width: 95vw;
    margin: 0.5rem;
  }
  
  .modal-header {
    padding: 1rem 1rem 0.75rem;
  }
  
  .modal-body {
    padding: 1rem;
  }
  
  .modal-footer {
    padding: 0.75rem 1rem 1rem;
    flex-direction: column;
  }
  
  .btn-cancel,
  .btn-confirm,
  .btn-success,
  .btn-danger {
    width: 100%;
    min-width: auto;
  }
}
.teacher-panel {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.panel-header {
  background: #ffffff;
  backdrop-filter: blur(10px);
  padding: 2rem;
  border-radius: 0 0 20px 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
}

.header-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
}

.logo-section {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-left: -2rem;
}

.header-logo {
  width: 500px;
  height: 130px;
  object-fit: contain;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.guide-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #ffffff;
  color: #2c3e50;
  text-decoration: none;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 25px;
  border: 2px solid rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  white-space: nowrap;
  min-height: calc(40px + 1.5rem);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.guide-link:hover {
  background: #f8f9fa;
  border-color: #4CAF50;
  color: #2e7d32;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.15);
}

.profile-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: rgba(255, 255, 255, 0.2);
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.profile-info:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

.profile-avatar {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #FF9800, #FF5722);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 1.1rem;
}

.profile-details {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.profile-name {
  color: #333;
  font-weight: 600;
  font-size: 1rem;
}

.profile-role {
  color: #666;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.profile-arrow {
  color: #333;
  font-size: 0.8rem;
  transition: transform 0.3s ease;
}

.profile-info:hover .profile-arrow {
  transform: rotate(180deg);
}

.panel-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
}

/* Hoca Panel responsive tasarım */
@media (max-width: 1024px) {
  .panel-content {
    max-width: 100%;
    padding: 1.5rem;
  }
}

@media (max-width: 768px) {
  .panel-content {
    padding: 1rem;
    margin: 0;
  }
  
  .panel-header {
    padding: 1.5rem;
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
  
  .header-content h1 {
    font-size: 2rem;
}

.welcome-card {
    padding: 1.5rem;
    grid-template-columns: 1fr;
    text-align: center;
    gap: 1rem;
  }
  
  .welcome-avatar {
    width: 80px;
    height: 80px;
    font-size: 3rem;
    margin: 0 auto;
  }
  
  .welcome-info h2 {
    font-size: 1.5rem;
  }
  
  .welcome-stats {
    justify-content: center;
    gap: 1.5rem;
  }
  
  .quick-access-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
}

@media (max-width: 480px) {
  .panel-content {
    padding: 0.75rem;
    margin: 0;
  }
  
  .panel-header {
    padding: 1rem;
    border-radius: 0 0 15px 15px;
  }
  
  .header-content h1 {
    font-size: 1.75rem;
  }
  
  .welcome-card {
    padding: 1rem;
    margin-bottom: 1.5rem;
  }
  
  .welcome-avatar {
    width: 70px;
    height: 70px;
    font-size: 2.5rem;
  }
  
  .welcome-info h2 {
    font-size: 1.25rem;
  }
  
  .welcome-stats {
    flex-direction: column;
    gap: 1rem;
  }
  
  .stat-number {
    font-size: 1.5rem;
  }
}

.welcome-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 2.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 2rem;
  align-items: center;
}

.welcome-avatar {
  font-size: 4rem;
  background: linear-gradient(135deg, #FF9800, #FF5722);
  border-radius: 50%;
  width: 100px;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.welcome-info h2 {
  margin: 0 0 0.5rem 0;
  color: #333;
  font-size: 2rem;
}

.welcome-role {
  margin: 0 0 0.5rem 0;
  color: #FF9800;
  font-weight: 600;
  font-size: 1.1rem;
}

.welcome-message {
  margin: 0;
  color: #666;
}

.welcome-stats {
  display: flex;
  gap: 2rem;
}

.stat-item {
  text-align: center;
}

.stat-number {
  display: block;
  font-size: 2rem;
  font-weight: 700;
  color: #333;
}

.stat-label {
  font-size: 0.9rem;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.quick-access-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.quick-card {
  background: #ffffff;
  border-radius: 20px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  border: 2px solid transparent;
}

.quick-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
}

.quick-card.primary {
  border-color: #4CAF50;
}

.quick-card.secondary {
  border-color: #2196F3;
}

.quick-card.success {
  border-color: #FF9800;
}

.quick-card.info {
  border-color: #9C27B0;
}

.card-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.quick-card h3 {
  margin: 0 0 1rem 0;
  color: #333;
  font-size: 1.3rem;
}

.quick-card p {
  margin: 0;
  color: #666;
  line-height: 1.5;
}

.section-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  margin-bottom: 2rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

/* Section card responsive tasarım */
@media (max-width: 768px) {
  .section-card {
    margin-bottom: 1.5rem;
    border-radius: 15px;
  }
  
  .section-header {
    padding: 1.5rem;
  }
  
  .section-header h3 {
    font-size: 1.25rem;
  }
  
  .section-header p {
    font-size: 0.9rem;
  }
  
  .section-content {
    padding: 1.5rem;
  }
}

@media (max-width: 480px) {
  .section-card {
    margin-bottom: 1rem;
    border-radius: 12px;
  }
  
  .section-header {
    padding: 1rem;
  }
  
  .section-header h3 {
    font-size: 1.125rem;
  }
  
  .section-header p {
    font-size: 0.85rem;
  }
  
  .section-content {
    padding: 1rem;
  }
}

.section-header {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  padding: 2rem;
}

.section-header h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
}

.section-header p {
  margin: 0;
  opacity: 0.9;
}

.section-content {
  padding: 2rem;
}

.no-data {
  text-align: center;
  padding: 3rem;
  color: #666;
}

.no-data-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.appointments-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.appointment-item {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 1.5rem;
  align-items: center;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 15px;
  border-left: 4px solid #4CAF50;
}

/* Appointment item responsive tasarım */
@media (max-width: 768px) {
  .appointment-item {
    grid-template-columns: 1fr;
    gap: 1rem;
    padding: 1rem;
    text-align: center;
  }
  
  .appointment-time {
    order: 1;
  }
  
  .appointment-details {
    order: 2;
  }
  
  .status-badge {
    order: 3;
    justify-self: center;
  }
}

@media (max-width: 480px) {
  .appointment-item {
    padding: 0.75rem;
    gap: 0.75rem;
  }
  
  .time-main {
    font-size: 1.25rem;
  }
  
  .time-day {
    font-size: 0.8rem;
  }
}

.appointment-time {
  text-align: center;
}

.time-main {
  font-size: 1.5rem;
  font-weight: 700;
  color: #333;
}

.time-day {
  font-size: 0.9rem;
  color: #666;
  margin-top: 0.25rem;
}

.appointment-details h4 {
  margin: 0 0 0.5rem 0;
  color: #333;
}

.student-name {
  margin: 0 0 0.5rem 0;
  color: #666;
}

.status-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
}

.status-beklemede {
  background: #FFF3E0;
  color: #F57C00;
}

.status-onaylandı {
  background: #E8F5E8;
  color: #388E3C;
}

.status-iptal-edildi {
  background: #FFEBEE;
  color: #D32F2F;
}

.cancel-btn {
  background: #f44336;
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1.2rem;
}

.cancel-btn:hover {
  background: #d32f2f;
  transform: scale(1.1);
}

.schedule-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 10px;
}

.summary-label {
  font-weight: 600;
  color: #333;
}

.summary-value {
  font-size: 1.2rem;
  font-weight: 700;
  color: #4CAF50;
}

/* Responsive Design */
@media (max-width: 768px) {
  .welcome-card {
    grid-template-columns: 1fr;
    text-align: center;
  }
  
  .welcome-stats {
    justify-content: center;
  }
  
  .quick-access-grid {
    grid-template-columns: 1fr;
  }
  
  .appointment-item {
    grid-template-columns: 1fr;
    text-align: center;
  }
  
  .schedule-summary {
    grid-template-columns: 1fr;
  }
}

.retry-button {
  background: #4CAF50;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
  margin-top: 1rem;
}

.retry-button:hover {
  background: #45a049;
  transform: translateY(-2px);
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 20px;
  max-width: 700px;
  width: 90%;
  max-height: 80vh;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-header {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  padding: 1.25rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.3rem;
}

.modal-close {
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  transition: background-color 0.3s ease;
}

.modal-close:hover {
  background: rgba(255, 255, 255, 0.2);
}

.modal-body {
  padding: 1.5rem;
}

.profile-section {
  text-align: center;
}

.profile-avatar-large {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #FF9800, #FF5722);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 2rem;
  margin: 0 auto 1.5rem;
}

.profile-info-details {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 10px;
}

.info-label {
  font-weight: 600;
  color: #333;
}

.info-value {
  color: #666;
  font-weight: 500;
}

.modal-actions {
  padding: 1.25rem;
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  border-top: 1px solid #eee;
}

.logout-button {
  background: #f44336;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
  margin-bottom: 12px; /* Alt boşluğu hafif artırıldı */
}

.logout-button:hover {
  background: #d32f2f;
  transform: translateY(-2px);
}

.btn-secondary {
  background: #6c757d;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn-secondary:hover {
  background: #5a6268;
  transform: translateY(-2px);
}

/* Loading Spinner */
.loading-container {
  text-align: center;
  padding: 3rem;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #4CAF50;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Profil Modal Stilleri */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 16px;
  max-width: 700px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #f0f0f0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 16px 16px 0 0;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
}

.modal-close {
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: background-color 0.3s;
}

.modal-close:hover {
  background: rgba(255, 255, 255, 0.2);
}

.modal-body {
  padding: 1.2rem;
}

.profile-section {
  text-align: center;
  margin-bottom: 1.5rem;
}

.profile-avatar-large {
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  font-weight: bold;
  color: white;
  margin: 0 auto 1rem;
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
}

.profile-info-grid {
  display: grid;
  gap: 1rem;
  text-align: left;
  max-width: 600px;
  margin: 0 auto;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #667eea;
  min-height: 60px;
}

.info-item label {
  font-weight: 600;
  color: #495057;
  min-width: 140px;
  font-size: 1rem;
}

.info-item span {
  color: #212529;
  font-weight: 500;
}

.role-badge {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
}

.profile-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin: 1.5rem auto;
  max-width: 600px;
}

.stat-card {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 1rem;
  text-align: center;
  border: 1px solid #e9ecef;
  transition: transform 0.3s, box-shadow 0.3s;
}

.stat-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  font-size: 2rem;
  margin-bottom: 0.5rem;
}

.stat-number {
  display: block;
  font-size: 1.5rem;
  font-weight: bold;
  color: #667eea;
  margin-bottom: 0.25rem;
}

.stat-label {
  font-size: 0.9rem;
  color: #6c757d;
  font-weight: 500;
}

.profile-actions {
  text-align: center;
}

.logout-button {
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 25px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 4px 15px rgba(220, 53, 69, 0.3);
}

.logout-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(220, 53, 69, 0.4);
}

/* Responsive tasarım */
@media (max-width: 768px) {
  .modal-content {
    width: 95%;
    margin: 1rem;
  }
  
  .profile-stats {
    grid-template-columns: 1fr;
  }
  
  .info-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .info-item label {
    min-width: auto;
  }
}

/* Çıkış Onay Popup Stilleri */
.logout-confirm-popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 3000;
  animation: fadeIn 0.3s ease-out;
}

.logout-confirm-popup {
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
  max-width: 700px;
  width: 90%;
  overflow: hidden;
  animation: slideIn 0.3s ease-out;
}

.logout-confirm-popup-header {
  background: linear-gradient(135deg, #9c27b0, #673ab7);
  color: white;
  padding: 2rem 2rem 1.5rem;
  text-align: center;
}

.logout-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
}

.logout-confirm-popup-header h3 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.logout-confirm-popup-body {
  padding: 2rem;
  text-align: center;
}

.logout-confirm-popup-body p {
  margin: 0;
  font-size: 1.1rem;
  color: #333;
}

.logout-confirm-popup-actions {
  padding: 1.5rem 2rem 2rem;
  text-align: center;
  border-top: 1px solid #eee;
  display: flex;
  gap: 1rem;
  justify-content: center;
}

.logout-confirm-popup .btn-cancel {
  background: #f5f5f5;
  color: #333;
  border: 1px solid #ddd;
  padding: 0.875rem 2rem;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 120px;
}

.logout-confirm-popup .btn-cancel:hover {
  background: #e0e0e0;
  transform: translateY(-2px);
}

.btn-logout-confirm {
  background: linear-gradient(135deg, #9c27b0, #673ab7);
  color: white;
  border: none;
  padding: 0.875rem 2rem;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(156, 39, 176, 0.3);
  min-width: 120px;
}

.btn-logout-confirm:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(156, 39, 176, 0.4);
}

.btn-logout-confirm:active {
  transform: translateY(0);
}

/* Animasyonlar */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideIn {
  from { 
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to { 
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Şifre güncelleme stilleri */
.password-input-group {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  margin-top: 0.5rem;
}

.password-input {
  flex: 1;
  padding: 0.75rem;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: white;
}

.password-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.update-password-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.update-password-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.update-password-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* Randevu Aksiyon Butonları */
.appointment-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

.btn-mark-absent,
.btn-mark-present {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.btn-mark-absent {
  background: linear-gradient(135deg, #dc3545, #c82333);
  color: white;
}

.btn-mark-absent:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(220, 53, 69, 0.3);
}

.btn-mark-present {
  background: linear-gradient(135deg, #28a745, #20c997);
  color: white;
}

.btn-mark-present:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
}

.btn-mark-absent:disabled,
.btn-mark-present:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* Status Badge Styles */
.status-öğrenci\ katılmadı {
  background: linear-gradient(135deg, #e74c3c, #c0392b);
  color: white;
  border: 1px solid #c0392b;
}

/* Haftalık Program Özeti Stilleri */
.week-info-header {
  text-align: center;
  margin-bottom: 2rem;
  padding: 1rem;
  background: linear-gradient(135deg, #667eea, #764ba2);
  border-radius: 15px;
  color: white;
}

.week-info-header h4 {
  margin: 0 0 0.5rem 0;
  font-size: 1.2rem;
  font-weight: 600;
}

.week-number {
  font-size: 0.9rem;
  opacity: 0.9;
  background: rgba(255, 255, 255, 0.2);
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.stat-card {
  display: flex;
  align-items: center;
  padding: 1.5rem;
  border-radius: 15px;
  background: white;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

.stat-card.primary {
  border-left: 4px solid #667eea;
}

.stat-card.success {
  border-left: 4px solid #28a745;
}

.stat-card.warning {
  border-left: 4px solid #ffc107;
}

.stat-card.danger {
  border-left: 4px solid #dc3545;
}

.stat-icon {
  font-size: 2rem;
  margin-right: 1rem;
}

.stat-content {
  flex: 1;
}

.stat-number {
  font-size: 2rem;
  font-weight: 700;
  color: #333;
  line-height: 1;
}

.stat-label {
  font-size: 0.9rem;
  color: #666;
  margin-top: 0.3rem;
}

.daily-breakdown {
  margin-bottom: 2rem;
}

.daily-breakdown h5 {
  text-align: center;
  margin-bottom: 1rem;
  color: #333;
  font-size: 1.1rem;
}

.daily-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 0.8rem;
}

.day-stat {
  text-align: center;
  padding: 1rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
}

.day-stat:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.12);
}

.day-name {
  font-weight: 600;
  color: #333;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
}

.day-numbers {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.day-numbers .total {
  font-size: 1.2rem;
  font-weight: 700;
  color: #667eea;
}

.day-numbers .confirmed {
  font-size: 0.8rem;
  color: #28a745;
}

.day-numbers .cancelled {
  font-size: 0.8rem;
  color: #dc3545;
}

.day-numbers .absent {
  font-size: 0.8rem;
  color: #ffc107;
}



.no-data {
  text-align: center;
  padding: 3rem 2rem;
  color: #666;
}

.no-data-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

/* Logo Responsive Tasarım */
@media (max-width: 1024px) {
  .header-logo {
    width: 400px;
    height: 104px;
  }
}

@media (max-width: 768px) {
  .panel-header {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
    padding: 1.5rem;
  }
  
  .header-content {
    align-items: center;
    text-align: center;
  }
  
  .logo-section {
    flex-direction: column;
    gap: 0.5rem;
    text-align: center;
    justify-content: center;
    margin-left: 0;
  }
  
  .header-logo {
    width: 400px;
    height: 120px;
  }
  
  .header-actions {
    justify-content: center;
    margin-top: 0.5rem;
  }
  
  .profile-info {
    padding: 0.75rem 1rem;
    justify-content: center;
    width: fit-content;
    margin: 0 auto;
  }
  
  .profile-avatar {
    width: 35px;
    height: 35px;
    font-size: 1rem;
  }
  
  .profile-name {
    font-size: 0.9rem;
  }
  
  .profile-role {
    font-size: 0.75rem;
  }
  
  /* Mevcut mobil CSS */
  .password-input-group {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .password-input {
    width: 100%;
  }
  
  .update-password-btn {
    width: 100%;
    padding: 0.875rem 1rem;
  }
  
  /* Randevu aksiyon butonları mobil */
  .appointment-actions {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .btn-mark-absent,
  .btn-mark-present {
    width: 100%;
    padding: 0.75rem 1rem;
    font-size: 0.9rem;
  }
  
  .logout-confirm-popup {
    max-width: 90%;
    margin: 1rem;
  }
  
  .logout-confirm-popup-header {
    padding: 1.5rem 1.5rem 1rem;
  }
  
  .logout-icon {
    font-size: 3rem;
  }
  
  .logout-confirm-popup-header h3 {
    font-size: 1.3rem;
  }
  
  .logout-confirm-popup-body {
    padding: 1.5rem;
  }
  
  .logout-confirm-popup-actions {
    padding: 1rem 1.5rem 1.5rem;
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .logout-confirm-popup .btn-cancel,
  .btn-logout-confirm {
    padding: 0.75rem 1.5rem;
    font-size: 0.95rem;
    min-width: auto;
  }
}

/* Küçük Mobil (≤480px) */
@media (max-width: 480px) {
  .panel-header {
    padding: 1rem;
    gap: 0.75rem;
  }
  
  .header-logo {
    width: 400px;
    height: 120px;
  }
  
  .profile-info {
    padding: 0.5rem 0.75rem;
    gap: 0.75rem;
  }
  
  .profile-avatar {
    width: 30px;
    height: 30px;
    font-size: 0.9rem;
  }
  
  .profile-name {
    font-size: 0.8rem;
  }
  
  .profile-role {
    font-size: 0.7rem;
  }
}

/* Ekstra Küçük Mobil (≤360px) */
@media (max-width: 360px) {
  .panel-header {
    padding: 0.75rem;
    gap: 0.5rem;
  }
  
  .header-logo {
    width: 250px;
    height: 80px;
  }
  
  .profile-info {
    padding: 0.5rem 0.5rem;
    gap: 0.5rem;
  }
  
  .profile-avatar {
    width: 28px;
    height: 28px;
    font-size: 0.8rem;
  }
  
  .profile-name {
    font-size: 0.75rem;
  }
  
  .profile-role {
    font-size: 0.65rem;
  }
}
</style>

